import { AUDIO_EXTENSIONS, AUDIO_TYPES, VIDEO_EXTENSIONS, VIDEO_TYPES, isHLSSrc, HLS_VIDEO_EXTENSIONS, HLS_VIDEO_TYPES } from './chunk-FRNAFKFA.js';
import { isHLSSupported, canPlayHLSNatively } from './chunk-KKTONNDY.js';
import { TextTrack } from './chunk-GHCUWWGF.js';
import { getRequestCredentials, preconnect } from './chunk-IVW6GMZV.js';
import { useMedia } from './chunk-ZIBTY3GT.js';
import { signal, peek, computed, effect, tick } from 'maverick.js';
import { defineElement, Component } from 'maverick.js/element';
import { setStyle, isString, isArray } from 'maverick.js/std';
import { $$_create_walker, $$_effect, $$_attr, $$_ref, $$_create_template } from 'maverick.js/dom';

var $$_templ = /* @__PURE__ */ $$_create_template(`<!$><audio preload="none" aria-hidden="true"></audio>`);
var $$_templ_2 = $$_templ;
var AudioProviderLoader = class {
  canPlay({ src, type }) {
    return isString(src) ? AUDIO_EXTENSIONS.test(src) || AUDIO_TYPES.has(type) || src.startsWith("blob:") && type === "audio/object" : type === "audio/object";
  }
  mediaType() {
    return "audio";
  }
  async load() {
    return new (await import('./provider-HKSF5X6Q.js')).AudioProvider(this.Bd);
  }
  render($store) {
    return (() => {
      const [$$_root, $$_walker] = $$_create_walker($$_templ_2);
      $$_effect(() => $$_attr($$_root, "controls", $store.controls()));
      $$_effect(() => $$_attr($$_root, "crossorigin", $store.crossorigin()));
      $$_ref($$_root, (el) => void (this.Bd = el));
      return $$_root;
    })();
  }
};
var $$_templ2 = /* @__PURE__ */ $$_create_template(`<!$><video preload="none" aria-hidden="true"></video>`);
var $$_templ_22 = $$_templ2;
var VideoProviderLoader = class {
  canPlay(src) {
    return isString(src.src) ? VIDEO_EXTENSIONS.test(src.src) || VIDEO_TYPES.has(src.type) || src.src.startsWith("blob:") && src.type === "video/object" || isHLSSrc(src) && canPlayHLSNatively() : src.type === "video/object";
  }
  mediaType() {
    return "video";
  }
  async load(context) {
    return new (await import('./provider-FPEHLPEB.js')).VideoProvider(this.k, context);
  }
  render($store) {
    const $poster = computed(
      () => (
        // === `true` because it's `null` to start with until we know if the poster can load.
        $store.poster() && $store.canLoadPoster() === true ? $store.poster() : null
      )
    );
    return (() => {
      const [$$_root, $$_walker] = $$_create_walker($$_templ_22);
      $$_effect(() => $$_attr($$_root, "controls", $store.controls()));
      $$_effect(() => $$_attr($$_root, "crossorigin", $store.crossorigin()));
      $$_effect(() => $$_attr($$_root, "poster", $poster()));
      $$_ref($$_root, (el) => void (this.k = el));
      return $$_root;
    })();
  }
};

// src/player/core/providers/hls/loader.tsx
var _HLSProviderLoader = class extends VideoProviderLoader {
  preconnect() {
    preconnect("https://cdn.jsdelivr.net", "preconnect");
  }
  canPlay({ src, type }) {
    return _HLSProviderLoader.supported && isString(src) && (HLS_VIDEO_EXTENSIONS.test(src) || HLS_VIDEO_TYPES.has(type));
  }
  async load(context) {
    return new (await import('./provider-KYXJPONW.js')).HLSProvider(this.k, context);
  }
};
var HLSProviderLoader = _HLSProviderLoader;
HLSProviderLoader.supported = isHLSSupported();

// src/player/core/outlet/source-select.ts
var SourceSelection = class {
  constructor(_domSources, _media, _loader) {
    this.pb = _domSources;
    this.a = _media;
    this.Fa = _loader;
    const HLS_LOADER = new HLSProviderLoader(), VIDEO_LOADER = new VideoProviderLoader(), AUDIO_LOADER = new AudioProviderLoader();
    this.Cd = computed(() => {
      return _media.$props.preferNativeHLS() ? [VIDEO_LOADER, AUDIO_LOADER, HLS_LOADER] : [HLS_LOADER, VIDEO_LOADER, AUDIO_LOADER];
    });
    effect(this.Xf.bind(this));
    effect(this.Yf.bind(this));
    effect(this.Zf.bind(this));
    effect(this._f.bind(this));
  }
  Xf() {
    this.a.delegate.d("sources-change", {
      detail: [...normalizeSrc(this.a.$props.src()), ...this.pb()]
    });
  }
  Yf() {
    const { $store } = this.a;
    const sources = $store.sources(), currentSource = peek($store.source), newSource = this.Dd(currentSource, sources), noMatch = sources[0]?.src && !newSource.src && !newSource.type;
    if (noMatch) {
      const { crossorigin } = $store, credentials = getRequestCredentials(crossorigin()), abort = new AbortController();
      Promise.all(
        sources.map(
          (source) => isString(source.src) && source.type === "?" ? fetch(source.src, {
            method: "HEAD",
            credentials,
            signal: abort.signal
          }).then((res) => {
            source.type = res.headers.get("content-type") || "??";
            return source;
          }).catch(() => source) : source
        )
      ).then((sources2) => {
        if (abort.signal.aborted)
          return;
        this.Dd(peek($store.source), sources2);
        tick();
      });
      return () => abort.abort();
    }
    tick();
  }
  Dd(currentSource, sources) {
    let newSource = { src: "", type: "" }, newLoader = null;
    for (const src of sources) {
      const loader = peek(this.Cd).find((loader2) => loader2.canPlay(src));
      if (loader) {
        newSource = src;
        newLoader = loader;
      }
    }
    this.$f(currentSource, newSource, newLoader);
    this.ag(peek(this.Fa), newLoader);
    return newSource;
  }
  $f(currentSource, newSource, newLoader) {
    if (newSource.src === currentSource.src && newSource.type === currentSource.type)
      return;
    this.a.delegate.d("source-change", { detail: newSource });
    this.a.delegate.d("media-type-change", {
      detail: newLoader?.mediaType(newSource) || "unknown"
    });
  }
  ag(currentLoader, newLoader) {
    if (newLoader === currentLoader)
      return;
    this.a.delegate.d("provider-change", { detail: null });
    newLoader && peek(() => newLoader.preconnect?.(this.a));
    this.Fa.set(newLoader);
    this.a.delegate.d("provider-loader-change", { detail: newLoader });
  }
  Zf() {
    const provider = this.a.$provider();
    if (!provider)
      return;
    if (this.a.$store.canLoad()) {
      peek(
        () => provider.setup({
          ...this.a,
          player: this.a.player
        })
      );
      return;
    }
    peek(() => provider.preconnect?.(this.a));
  }
  _f() {
    const provider = this.a.$provider(), source = this.a.$store.source();
    if (this.a.$store.canLoad()) {
      peek(() => provider?.loadSource(source, peek(this.a.$store.preload)));
      return;
    }
    try {
      isString(source.src) && preconnect(new URL(source.src).origin, "preconnect");
    } catch (e) {
    }
  }
};
function normalizeSrc(src) {
  return (isArray(src) ? src : [!isString(src) && "src" in src ? src : { src }]).map(
    ({ src: src2, type }) => ({
      src: src2,
      type: type ?? (!isString(src2) || src2.startsWith("blob:") ? "video/object" : "?")
    })
  );
}
var Tracks = class {
  constructor(_domTracks, _media) {
    this.qb = _domTracks;
    this.a = _media;
    this.Ed = [];
    effect(this.bg.bind(this));
  }
  bg() {
    const newTracks = [...this.a.$props.textTracks(), ...this.qb()];
    for (const newTrack of newTracks) {
      const id = newTrack.id || TextTrack.createId(newTrack);
      if (!this.a.textTracks.getById(id)) {
        newTrack.id = id;
        this.a.textTracks.add(newTrack);
      }
    }
    for (const oldTrack of this.Ed) {
      if (!newTracks.some((t) => t.id === oldTrack.id)) {
        const track = oldTrack.id && this.a.textTracks.getById(oldTrack.id);
        if (track)
          this.a.textTracks.remove(track);
      }
    }
    this.Ed = newTracks;
  }
};

// src/player/core/outlet/outlet.tsx
var Outlet = class extends Component {
  constructor(instance) {
    super(instance);
    this.pb = signal([]);
    this.qb = signal([]);
    this.Fa = signal(null);
    this.K = -1;
    this.a = useMedia();
    new SourceSelection(this.pb, this.a, this.Fa);
    new Tracks(this.qb, this.a);
  }
  onAttach(el) {
    el.setAttribute("keep-alive", "");
  }
  onConnect(el) {
    this.Ga();
    this.rb();
    const resize = new ResizeObserver(this.Ga.bind(this));
    resize.observe(el);
    const mutation = new MutationObserver(this.rb.bind(this));
    mutation.observe(el, { attributes: true, childList: true });
    return () => {
      resize.disconnect();
      mutation.disconnect();
    };
  }
  onDestroy() {
    this.a.$store.currentTime.set(0);
  }
  Ga() {
    if (this.K >= 0)
      return;
    this.K = window.requestAnimationFrame(() => {
      const player = this.a.player, width = this.el.offsetWidth, height = this.el.offsetHeight;
      setStyle(player, "--media-width", width + "px");
      setStyle(player, "--media-height", height + "px");
      this.K = -1;
    });
  }
  rb() {
    const sources = [], tracks = [], children = this.el.children;
    for (const el of children) {
      if (el instanceof HTMLSourceElement) {
        sources.push({
          src: el.src,
          type: el.type
        });
      } else if (el instanceof HTMLTrackElement) {
        tracks.push({
          id: el.id,
          src: el.src,
          kind: el.track.kind,
          language: el.srclang,
          label: el.label,
          default: el.default,
          type: el.getAttribute("data-type")
        });
      }
    }
    this.pb.set(sources);
    this.qb.set(tracks);
  }
  render() {
    return () => {
      const loader = this.Fa();
      if (!loader)
        return null;
      const el = loader.render(this.a.$store);
      peek(() => {
        loader.load(this.a).then((provider) => {
          if (peek(this.Fa) !== loader)
            return;
          this.a.delegate.d("provider-change", {
            detail: provider
          });
        });
      });
      return el;
    };
  }
};
Outlet.el = defineElement({
  tagName: "media-outlet"
});

export { Outlet };
